/**
 * @fileoverview Snapchat Client Intake Form
 * Simple form for collecting basic client information.
 * Client ID is auto-generated on the backend.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import './CallForm/CallForm.css';

/**
 * SnapchatIntakeForm component for collecting basic client information.
 * Features:
 * - First and Last name collection
 * - Email validation
 * - Client ID auto-generated on backend
 * - Form validation and error handling
 * 
 * @returns {React.ReactElement} The intake form interface
 */
const SnapchatIntakeForm = () => {
  const { partnerSlug } = useParams();
  const [partner, setPartner] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerError, setPartnerError] = useState(null);
  
  const [formData, setFormData] = useState({
    First_Legal_Name: '',
    Last_Legal_Name: '',
    Email_Address: '',
    Snap_Monetized: null
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Load partner if slug is in URL
  useEffect(() => {
    if (partnerSlug) {
      loadPartner();
    }
  }, [partnerSlug]);

  const loadPartner = async () => {
    try {
      setPartnerLoading(true);
      const response = await axios.get(API_ENDPOINTS.PARTNERS.BY_SLUG(partnerSlug));
      setPartner(response.data);
      setPartnerError(null);
    } catch (err) {
      console.error('Error loading partner:', err);
      setPartnerError('Partner not found');
    } finally {
      setPartnerLoading(false);
    }
  };

  /**
   * Handles form field changes.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  /**
   * Validates form data before submission.
   * @returns {boolean} Whether the form is valid
   */
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.First_Legal_Name.trim()) {
      newErrors.First_Legal_Name = 'First name is required';
    }
    
    if (!formData.Last_Legal_Name.trim()) {
      newErrors.Last_Legal_Name = 'Last name is required';
    }
    
    if (!formData.Email_Address.trim()) {
      newErrors.Email_Address = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email_Address)) {
      newErrors.Email_Address = 'Please enter a valid email address';
    } else {
      // Additional email validation
      const email = formData.Email_Address.trim().toLowerCase();
      
      // Check for common fake/throwaway email patterns
      const suspiciousPatterns = [
        'test@test',
        'fake@',
        'noemail@',
        'none@',
        'example@',
        '@test.',
        '@fake.',
        '@none.',
        '@example.',
        'asdf@',
        'qwerty@',
        '123@',
        'temp@',
        'temporary@'
      ];
      
      if (suspiciousPatterns.some(pattern => email.includes(pattern))) {
        newErrors.Email_Address = 'Please provide a real email address that you have access to';
      }
      
      // Check for valid TLD (top-level domain)
      const tldMatch = email.match(/\.([a-z]{2,})$/i);
      if (!tldMatch || tldMatch[1].length < 2) {
        newErrors.Email_Address = 'Please enter a valid email with a proper domain (e.g., .com, .net, .org)';
      }
      
      // Check for disposable email domains
      const disposableDomains = [
        'tempmail.com',
        'throwaway.email',
        '10minutemail.com',
        'guerrillamail.com',
        'mailinator.com',
        'trashmail.com',
        'yopmail.com',
        'getnada.com'
      ];
      
      const domain = email.split('@')[1];
      if (disposableDomains.includes(domain)) {
        newErrors.Email_Address = 'Please use a permanent email address, not a temporary/disposable one';
      }
    }
    
    if (formData.Snap_Monetized === null) {
      newErrors.Snap_Monetized = 'Please select Yes or No';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission.
   * @param {React.FormEvent<HTMLFormElement>} e - The form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(API_ENDPOINTS.CLIENTS.INTAKE, {
        First_Legal_Name: formData.First_Legal_Name.trim(),
        Last_Legal_Name: formData.Last_Legal_Name.trim(),
        Email_Address: formData.Email_Address.trim(),
        Snap_Monetized: formData.Snap_Monetized,
        partner_id: partner?._id || partner?.id || null
      });
      
      if (response.data.status === 'success') {
        setResult(response.data);
      }
    } catch (error) {
      console.error('Error submitting intake form:', error);
      setErrors({ submit: error.response?.data?.detail || 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Success view
  if (result) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ 
              fontSize: '64px', 
              marginBottom: '24px',
              animation: 'fadeIn 0.5s ease-out'
            }}>✓</div>
            <h1 className="snapped-call-form__title" style={{ marginBottom: '16px' }}>
              Client Created
            </h1>
            <div style={{ 
              fontSize: '1.1rem', 
              color: '#666',
              marginBottom: '8px',
              background: 'transparent'
            }}>
              <strong>{result.First_Legal_Name} {result.Last_Legal_Name}</strong>
            </div>
            <div style={{ 
              fontSize: '1rem', 
              color: '#666',
              marginBottom: '24px',
              background: 'transparent'
            }}>
              {result.Email_Address}
            </div>
            <div style={{ 
              fontSize: '1.5rem', 
              color: '#4962BF',
              fontWeight: '700',
              marginBottom: '32px',
              background: 'transparent'
            }}>
              Client ID: {result.client_id}
            </div>
            <button
              type="button"
              className="snapped-call-form__submit-button"
              onClick={() => {
                setResult(null);
                setFormData({
                  First_Legal_Name: '',
                  Last_Legal_Name: '',
                  Email_Address: '',
                  Snap_Monetized: null
                });
              }}
              style={{ maxWidth: '300px' }}
            >
              Add Another Client
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while fetching partner
  if (partnerSlug && partnerLoading) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ color: '#666', background: 'transparent' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error if partner not found
  if (partnerSlug && partnerError) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>❌</div>
            <h1 className="snapped-call-form__title">Partner Not Found</h1>
            <p style={{ color: '#666', background: 'transparent' }}>
              The partner "{partnerSlug}" does not exist.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="call-form-page call-form">
      <div className="snapped-call-form__container">
        <h1 className="snapped-call-form__title">Snapchat Client Intake Form</h1>
        {partner && (
          <p style={{ textAlign: 'center', color: '#4962BF', marginBottom: '32px', background: 'transparent', fontWeight: '600' }}>
            {partner.name}
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="snapped-call-form__section">
            <h3 className="snapped-call-form__section-heading">Client Information</h3>
            
            <input
              className={`snapped-call-form__input ${errors.First_Legal_Name ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="First_Legal_Name"
              placeholder="First Name *"
              value={formData.First_Legal_Name}
              onChange={handleChange}
              required
            />
            {errors.First_Legal_Name && (
              <span className="snapped-call-form__error-message">{errors.First_Legal_Name}</span>
            )}
            
            <input
              className={`snapped-call-form__input ${errors.Last_Legal_Name ? 'snapped-call-form__input--error' : ''}`}
              type="text"
              name="Last_Legal_Name"
              placeholder="Last Name *"
              value={formData.Last_Legal_Name}
              onChange={handleChange}
              required
            />
            {errors.Last_Legal_Name && (
              <span className="snapped-call-form__error-message">{errors.Last_Legal_Name}</span>
            )}
            
            <input
              className={`snapped-call-form__input ${errors.Email_Address ? 'snapped-call-form__input--error' : ''}`}
              type="email"
              name="Email_Address"
              placeholder="Email Address *"
              value={formData.Email_Address}
              onChange={handleChange}
              required
            />
            <div style={{ 
              marginTop: '8px', 
              marginBottom: '12px',
              padding: '12px',
              background: '#FFF9E6',
              border: '1px solid #FFD700',
              borderRadius: '6px',
              fontSize: '0.9rem',
              color: '#856404',
              lineHeight: '1.5'
            }}>
              <strong>⚠️ Important:</strong> All follow-up communications and action-required items will be sent to this email address. This inbox will only receive essential business correspondence and will not be used for marketing or promotional purposes.
            </div>
            {errors.Email_Address && (
              <span className="snapped-call-form__error-message">{errors.Email_Address}</span>
            )}
            
            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '12px', 
                fontSize: '1rem',
                color: '#333',
                background: 'transparent'
              }}>
                Are you monetized on Snapchat? *
              </label>
              <div style={{ display: 'flex', gap: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  background: 'transparent'
                }}>
                  <input
                    type="radio"
                    name="Snap_Monetized"
                    value="yes"
                    checked={formData.Snap_Monetized === true}
                    onChange={() => setFormData(prev => ({ ...prev, Snap_Monetized: true }))}
                    style={{ 
                      width: '18px', 
                      height: '18px',
                      accentColor: '#4962BF'
                    }}
                  />
                  <span style={{ fontSize: '1rem', background: 'transparent' }}>Yes</span>
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  background: 'transparent'
                }}>
                  <input
                    type="radio"
                    name="Snap_Monetized"
                    value="no"
                    checked={formData.Snap_Monetized === false}
                    onChange={() => setFormData(prev => ({ ...prev, Snap_Monetized: false }))}
                    style={{ 
                      width: '18px', 
                      height: '18px',
                      accentColor: '#4962BF'
                    }}
                  />
                  <span style={{ fontSize: '1rem', background: 'transparent' }}>No</span>
                </label>
                
              </div>
              {errors.Snap_Monetized && (
                <span className="snapped-call-form__error-message">{errors.Snap_Monetized}</span>
              )}
            </div>
          </div>
          
          {errors.submit && (
            <div className="snapped-call-form__error-message" style={{ marginBottom: '20px' }}>
              {errors.submit}
            </div>
          )}
          
          <button 
            type="submit" 
            className="snapped-call-form__submit-button"
            disabled={loading}
          >
            {loading ? 'Creating Client...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SnapchatIntakeForm;
